import { useRef, useState, useCallback } from 'react';
import type { MouseEvent, KeyboardEvent, FocusEvent } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';
import styled, { css } from 'styled-components';

import {
  Popover,
  TouchDialog,
  Icon,
  registerIcon,
  useOuterEvent,
  Menu,
  Button,
  defaultThemeProp,
  useI18n,
  Tooltip,
  calculateFontSize,
  isInstance,
  isTouchDevice
} from '@pega/cosmos-react-core';
import type { TranslationPack } from '@pega/cosmos-react-core';
import * as arrowMicroDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/arrow-micro-down.icon';

import useEscapeKey from '../hooks/useEscapeKey';
import useCloseOnEditorClick from '../hooks/useCloseOnEditorClick';

registerIcon(arrowMicroDownIcon);

const StyledFontSelect = styled(Button)(({ theme }) => {
  const fontSizes = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);
  return css`
    color: ${theme.base.palette['foreground-color']};
    display: inline-flex;
    align-items: center;
    height: calc(3.5 * ${theme.base.spacing});
    padding: 0 calc(0.5 * ${theme.base.spacing});
    font-size: ${fontSizes.xxs};
    min-width: 12ch;

    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `;
});

StyledFontSelect.defaultProps = defaultThemeProp;

const StyledSelectMenu = styled.div(({ theme }) => {
  return css`
    min-width: 16ch;
    max-height: calc(30 * ${theme.base.spacing});
  `;
});

StyledSelectMenu.defaultProps = defaultThemeProp;
type FontFamilyItem =
  | { id: string; labelKey: keyof TranslationPack; value: string; label?: never }
  | { id: string; label: string; value: string; labelKey?: never };

const FONT_FAMILIES: FontFamilyItem[] = [
  { id: 'default', labelKey: 'rte_default_font', value: '' },
  { id: 'arial', label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { id: 'courier', label: 'Courier New', value: '"Courier New", Courier, monospace' },
  { id: 'georgia', label: 'Georgia', value: 'Georgia, serif' },
  { id: 'times', label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { id: 'trebuchet', label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
  { id: 'verdana', label: 'Verdana', value: 'Verdana, Geneva, sans-serif' }
];

interface FontFamilySelectProps {
  editor: TiptapEditor;
  'data-testid'?: string;
}

const FontFamilySelect = ({ editor, ...restProps }: FontFamilySelectProps) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const selecting = useRef(false);
  const openedByClick = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const touchDialogRef = useRef<HTMLDivElement>(null);

  useOuterEvent('mousedown', [buttonRef, popoverRef, menuRef, touchDialogRef], () => {
    if (open) {
      setOpen(false);
    }
  });

  const handleEscapeClose = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  useEscapeKey(open, handleEscapeClose, editor);
  useCloseOnEditorClick(open, handleEscapeClose);

  const onFontSelect = (fontId: string, e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const font = FONT_FAMILIES.find(f => f.id === fontId);
    if (font) {
      if (font.value) {
        editor.chain().focus().setFontFamily(font.value).run();
      } else {
        editor.chain().focus().unsetFontFamily().run();
      }
    }

    if (!openedByClick.current) {
      buttonRef.current?.focus();
    }
    setOpen(false);
    selecting.current = true;
    setTimeout(() => {
      selecting.current = false;
      if (!editor.isFocused && openedByClick.current) {
        editor.commands.focus();
      }
    }, 0);
  };

  const getFontLabel = (font: FontFamilyItem): string => {
    if (font.labelKey) {
      return t(font.labelKey);
    }
    return font.label;
  };

  const getActiveFont = (): string => {
    const attrs = editor.getAttributes('textStyle');
    const { fontFamily } = attrs;
    if (typeof fontFamily !== 'string' || !fontFamily) return t('rte_default_font');
    const match = FONT_FAMILIES.find(f => f.value === fontFamily);
    return match ? getFontLabel(match) : fontFamily;
  };

  const getActiveFontId = (): string => {
    const attrs = editor.getAttributes('textStyle');
    const { fontFamily } = attrs;
    if (typeof fontFamily !== 'string' || !fontFamily) return 'default';
    const match = FONT_FAMILIES.find(f => f.value === fontFamily);
    return match?.id ?? 'default';
  };

  const menuContent = (
    <Menu
      items={FONT_FAMILIES.map(font => ({
        id: font.id,
        primary: getFontLabel(font),
        selected: font.id === getActiveFontId()
      }))}
      focusControlEl={isTouchDevice ? undefined : buttonRef.current ?? undefined}
      mode='single-select'
      ref={menuRef}
      onItemClick={onFontSelect}
    />
  );

  return (
    <>
      <StyledFontSelect
        {...restProps}
        variant='simple'
        type='button'
        icon={false}
        ref={buttonRef}
        data-toolbar-item='true'
        onMouseDown={(e: MouseEvent) => {
          e.preventDefault();
          if (!open) {
            openedByClick.current = true;
          }
          setOpen(!open);
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' && !selecting.current) {
            e.preventDefault();
            setOpen(true);
            openedByClick.current = false;
          } else if (e.key === 'Enter') {
            e.preventDefault();
            selecting.current = false;
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            setOpen(false);
          } else if (e.key === 'Escape' && open) {
            e.preventDefault();
            setOpen(false);
          }
        }}
        onFocus={(e: FocusEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onBlur={(e: FocusEvent) => {
          const { relatedTarget } = e;
          if (
            isInstance(relatedTarget, Node) &&
            !buttonRef.current?.contains(relatedTarget) &&
            !popoverRef.current?.contains(relatedTarget) &&
            !menuRef.current?.contains(relatedTarget) &&
            !touchDialogRef.current?.contains(relatedTarget)
          ) {
            setOpen(false);
          }
        }}
        onClick={(e: MouseEvent) => e.stopPropagation()}
        aria-expanded={open}
        aria-label={`${t('rte_font_family')}. ${getActiveFont()} ${t('selected').toLocaleLowerCase()}`}
        aria-haspopup
      >
        <span>{getActiveFont()}</span>
        <Icon name='arrow-micro-down' />
      </StyledFontSelect>
      {buttonRef.current && (
        <Tooltip target={buttonRef.current} showDelay='none' hideDelay='none'>
          {t('rte_font_family')}
        </Tooltip>
      )}
      {isTouchDevice ? (
        <TouchDialog
          ref={touchDialogRef}
          open={open}
          heading={t('rte_font_family')}
          onDismiss={() => setOpen(false)}
        >
          {menuContent}
        </TouchDialog>
      ) : (
        <Popover
          show={open}
          as={StyledSelectMenu}
          target={buttonRef.current}
          placement='bottom-start'
          ref={popoverRef}
        >
          {menuContent}
        </Popover>
      )}
    </>
  );
};

export default FontFamilySelect;
